import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { ShieldCheck } from '@phosphor-icons/react'
import { useUpdateAvailable, type UpdateState } from '../hooks/useUpdateAvailable'
import {
  UPDATE_MODAL_COPY,
  FALLBACK_VERSION_LABEL,
  type UpdateModalCopy,
} from './UpdateAvailableModal.copy'

type VisibleState = Exclude<UpdateState, 'hidden'>

function copyForState(state: VisibleState): UpdateModalCopy | null {
  if (state === 'success') return null
  return UPDATE_MODAL_COPY[state]
}

interface BannerMetaProps {
  metaTime: string
  version: string | null
  tone: 'purple' | 'emerald'
}

function BannerMeta({ metaTime, version, tone }: BannerMetaProps) {
  const slashColor =
    tone === 'emerald' ? 'text-[#10b981]' : 'text-[var(--bc-accent-400)]'
  return (
    <div className="mb-4 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-[11.5px] font-normal tabular-nums text-[var(--bc-text-quaternary)] -tracking-[0.005em]">
      <span className="font-light text-[var(--bc-text-tertiary)] -tracking-[0.02em]">
        better<span aria-hidden="true" className={`${slashColor} font-normal`}>/</span>close
      </span>
      <span className="opacity-50">·</span>
      <span>{version ?? FALLBACK_VERSION_LABEL}</span>
      <span className="opacity-50">·</span>
      <span>{metaTime}</span>
    </div>
  )
}

/**
 * UpdateAvailableModal — premium blocking modal for new production bundles.
 *
 * Rendered once at the root of App.tsx inside <ActiveAnalysisProvider>.
 * Reads its entire state from useUpdateAvailable(). Returns null when
 * state === 'hidden'. All decoration (backdrop, beam, particles, halo) is
 * CSS-driven via global keyframes in src/index.css. The canonical slash
 * uses var(--bc-accent-400) — the same token BrandLogo.tsx uses.
 */
export default function UpdateAvailableModal() {
  const { state, newVersion, confirmReload } = useUpdateAvailable()
  const prefersReducedMotion = useReducedMotion()

  if (state === 'hidden') return null

  const visibleState = state as VisibleState
  const isSuccess = visibleState === 'success'
  const isDeferred = visibleState === 'deferred'
  const isReloading = visibleState === 'reloading'
  const copy = copyForState(visibleState)

  const titleId = 'bc-update-modal-title'
  const descId = 'bc-update-modal-desc'

  const spring = prefersReducedMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 380, damping: 32, mass: 0.9 }

  const backdropVisible = !isSuccess

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="bc-update-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none fixed inset-0 z-[90]"
      >
        <div
          data-bc-backdrop=""
          data-visible={backdropVisible ? 'true' : 'false'}
          aria-hidden="true"
          className={`
            absolute inset-0 bg-black/70 backdrop-blur-[16px] backdrop-saturate-[120%]
            transition-opacity duration-[400ms]
            ${backdropVisible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}
          `}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 45%, rgba(168,85,247,0.18) 0%, transparent 60%), radial-gradient(circle at 30% 70%, rgba(232,121,249,0.08) 0%, transparent 60%)',
            }}
          />
        </div>

        <motion.div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descId}
          layout
          initial={{ opacity: 0, scale: 0.92, filter: 'blur(10px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, scale: 0.96, y: 20 }}
          transition={spring}
          className={`
            pointer-events-auto fixed z-[100]
            ${isSuccess
              ? 'bottom-[calc(6.5rem+env(safe-area-inset-bottom))] left-4 right-4 sm:bottom-7 sm:left-auto sm:right-7 sm:w-[330px]'
              : 'left-1/2 top-1/2 w-[calc(100vw-32px)] max-w-[420px] -translate-x-1/2 -translate-y-1/2 sm:max-w-[440px]'}
          `}
        >
          <BannerCard
            copy={copy}
            version={newVersion}
            isSuccess={isSuccess}
            isDeferred={isDeferred}
            isReloading={isReloading}
            titleId={titleId}
            descId={descId}
            onConfirm={confirmReload}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

interface BannerCardProps {
  copy: UpdateModalCopy | null
  version: string | null
  isSuccess: boolean
  isDeferred: boolean
  isReloading: boolean
  titleId: string
  descId: string
  onConfirm: () => void
}

function BannerCard({
  copy,
  version,
  isSuccess,
  isDeferred,
  isReloading,
  titleId,
  descId,
  onConfirm,
}: BannerCardProps) {
  const cardTone = isSuccess
    ? 'rounded-[20px] border-[rgba(16,185,129,0.3)] bg-[rgba(6,20,14,0.92)] p-[18px_20px] [animation:bc-card-breathe-success_4s_ease-in-out_infinite]'
    : 'rounded-[24px] border-white/[0.08] bg-[rgba(10,10,15,0.92)] p-[24px_22px_22px_22px] sm:p-[28px_28px_26px_28px] [animation:bc-card-breathe_7s_ease-in-out_infinite_3s]'

  return (
    <div
      className={`
        relative overflow-hidden border backdrop-blur-[32px] backdrop-saturate-[180%]
        transition-[padding,background,border-color,border-radius] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]
        ${cardTone}
      `}
    >
      {/* Particles */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.55]">
        {[
          { left: 12, delay: 0 },
          { left: 28, delay: 1.4 },
          { left: 48, delay: 0.6 },
          { left: 68, delay: 2.2 },
          { left: 85, delay: 1.1 },
        ].map(({ left, delay }) => (
          <span
            key={left}
            className={`absolute bottom-0 h-[2px] w-[2px] rounded-full ${
              isSuccess
                ? 'bg-[#10b981] shadow-[0_0_6px_#10b981]'
                : 'bg-[var(--bc-accent-400)] shadow-[0_0_6px_var(--bc-accent-400)]'
            }`}
            style={{
              left: `${left}%`,
              animation: 'bc-particle-float 5s ease-in-out infinite',
              animationDelay: `${delay}s`,
            }}
          />
        ))}
      </span>

      {/* Content — default variant */}
      {!isSuccess && copy && (
        <div className="relative z-[2] flex flex-col items-center text-center">
          <SlashIconBox isDeferred={isDeferred} isReloading={isReloading} />
          <h2
            id={titleId}
            className="mb-1.5 text-[18px] font-medium leading-[1.25] -tracking-[0.025em] text-[var(--bc-text-primary)] sm:text-[20px]"
          >
            {copy.title}
          </h2>
          <BannerMeta metaTime={copy.metaTime} version={version} tone="purple" />
          <p
            id={descId}
            className="mb-6 max-w-[300px] text-[13px] leading-[1.6] text-[var(--bc-text-secondary)] sm:max-w-[340px] sm:text-[13.5px]"
          >
            {copy.bodyLead}
            <span className="font-medium text-[var(--bc-text-primary)]">{copy.bodyHighlight}</span>
            {copy.bodyTail}
          </p>
          <div className="flex w-full flex-col items-center gap-2.5">
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeferred || isReloading}
              aria-busy={isReloading}
              className={ctaClass({ isDeferred, isReloading })}
            >
              <span className="relative z-[2]">{copy.cta}</span>
              {!isDeferred && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="relative z-[2] h-[17px] w-[17px]"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
              {!isDeferred && !isReloading && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 -left-[150%] h-full w-[60%]"
                  style={{
                    background:
                      'linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
                    transform: 'skewX(-20deg)',
                    animation: 'bc-cta-shimmer 3.5s ease-in-out infinite',
                  }}
                />
              )}
            </button>
            <div className="mt-1 flex items-center justify-center gap-1.5 text-[11px] font-normal tracking-[0.01em] text-[var(--bc-text-quaternary)]">
              <ShieldCheck size={12} weight="duotone" aria-hidden="true" />
              <span>{copy.helper}</span>
            </div>
          </div>
        </div>
      )}

      {/* Content — success variant (horizontal compact) */}
      {isSuccess && (
        <div className="relative z-[2] flex w-full items-center gap-3.5 text-left">
          <SuccessCheckBox />
          <div className="min-w-0 flex-1">
            <div
              id={titleId}
              className="mb-[3px] text-[14.5px] font-semibold leading-[1.3] -tracking-[0.015em] text-[var(--bc-text-primary)]"
            >
              {UPDATE_MODAL_COPY.success.title}
            </div>
            <BannerMeta metaTime="à jour" version={version} tone="emerald" />
            <span id={descId} className="sr-only">
              Nouvelle version chargée avec succès.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

function ctaClass({
  isDeferred,
  isReloading,
}: {
  isDeferred: boolean
  isReloading: boolean
}) {
  const base =
    'relative flex w-full min-h-[52px] max-w-[320px] items-center justify-center gap-2.5 overflow-hidden rounded-[14px] px-[22px] py-[15px] text-[14px] font-semibold -tracking-[0.01em] text-white transition-all duration-[220ms] ease-[cubic-bezier(0.4,0,0.2,1)] sm:min-h-[54px] sm:px-6 sm:py-4 sm:text-[15px]'

  if (isDeferred) {
    return `${base} cursor-not-allowed bg-[linear-gradient(135deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.04)_100%)] text-[var(--bc-text-tertiary)] shadow-[0_4px_14px_-4px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.08)]`
  }
  if (isReloading) {
    return `${base} cursor-progress bg-[linear-gradient(135deg,#10b981_0%,#059669_100%)] shadow-[0_10px_28px_-4px_rgba(16,185,129,0.65),inset_0_1px_0_rgba(255,255,255,0.22)]`
  }
  return `${base} cursor-pointer bg-[var(--bn-accent)] hover:bg-[var(--bn-accent-hover)] shadow-[var(--bn-cta-shadow)] hover:-translate-y-0.5 hover:shadow-[var(--bn-cta-shadow-hover)] active:translate-y-0 focus-visible:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]`
}

interface SlashIconBoxProps {
  isDeferred: boolean
  isReloading: boolean
}

function SlashIconBox({ isDeferred, isReloading }: SlashIconBoxProps) {
  const tone = isDeferred
    ? {
        box: 'border-white/25 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.22)_0%,transparent_70%),linear-gradient(135deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_0_44px_-4px_rgba(255,255,255,0.35)]',
        halo: 'bg-[radial-gradient(circle,rgba(255,255,255,0.22)_0%,transparent_55%)]',
        slashColor: 'text-[var(--bc-text-primary)]',
        slashShadow:
          'drop-shadow-[0_0_16px_rgba(255,255,255,0.5)] drop-shadow-[0_0_32px_rgba(255,255,255,0.28)]',
      }
    : {
        box: 'border-[var(--bn-accent-border-bold)] bg-[var(--bn-accent-bg-strong)] shadow-[var(--bn-cta-shadow)]',
        halo: 'bg-[var(--bn-accent-bg)]',
        slashColor: 'text-white',
        slashShadow:
          'drop-shadow-[0_0_14px_rgba(255,255,255,0.5)] drop-shadow-[0_0_28px_rgba(255,255,255,0.25)]',
      }

  return (
    <div
      className={`relative mb-[18px] flex h-16 w-16 items-center justify-center overflow-hidden rounded-[20px] border sm:mb-5 sm:h-[72px] sm:w-[72px] ${tone.box}`}
    >
      <span
        aria-hidden="true"
        className={`absolute -inset-[14px] -z-10 rounded-[34px] ${tone.halo} [animation:bc-icon-halo_3s_ease-in-out_infinite]`}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-[-100%] top-0 z-[1] h-full w-[60%] skew-x-[-20deg]"
        style={{
          background:
            'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)',
          animation: 'bc-icon-beam-sweep 4s ease-in-out infinite 2s',
        }}
      />
      {/*
        Brand slash — SVG line identical to public/favicon.svg (favicon is
        the authoritative brand mark). Line goes from (11,26) to (21,6) —
        midpoint is (16,16), perfectly centered in the 32x32 viewBox so
        rotation happens around the visual center with zero drift.

        Stroke color uses currentColor which the parent sets via slashColor.
        Entrance: 800ms stroke-dasharray draw-in (pen-stroke effect).
        Reloading: 800ms cubic-bezier rotation (premium acceleration curve).
      */}
      <svg
        aria-hidden="true"
        viewBox="0 0 32 32"
        fill="none"
        className={`relative z-[2] block h-[44px] w-[44px] sm:h-[52px] sm:w-[52px] ${tone.slashColor} ${tone.slashShadow} ${isReloading ? '[animation:bc-slash-spin_800ms_cubic-bezier(0.4,0,0.2,1)_infinite]' : ''}`}
        style={{ transformOrigin: 'center' }}
      >
        <line
          x1="11"
          y1="26"
          x2="21"
          y2="6"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          style={{
            strokeDasharray: 24,
            strokeDashoffset: 0,
            animation: isReloading
              ? 'none'
              : 'bc-slash-draw 800ms cubic-bezier(0.22, 1, 0.36, 1) both',
          }}
        />
      </svg>
    </div>
  )
}

function SuccessCheckBox() {
  return (
    <div
      className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] border border-[rgba(16,185,129,0.45)] shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_0_24px_-4px_rgba(16,185,129,0.55)]"
      style={{
        background:
          'radial-gradient(circle at 30% 30%, rgba(16,185,129,0.5) 0%, transparent 70%), linear-gradient(135deg, rgba(16,185,129,0.25) 0%, rgba(5,150,105,0.08) 100%)',
      }}
    >
      <span
        aria-hidden="true"
        className="absolute -inset-2.5 -z-10 rounded-[22px]"
        style={{
          background: 'radial-gradient(circle, rgba(16,185,129,0.3) 0%, transparent 55%)',
          animation: 'bc-icon-halo-success 2.4s ease-in-out infinite',
        }}
      />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="h-6 w-6"
        style={{ filter: 'drop-shadow(0 0 8px rgba(52,211,153,0.8))' }}
      >
        <circle
          cx="12"
          cy="12"
          r="11"
          stroke="#34d399"
          strokeWidth="2"
          fill="none"
          style={{
            strokeDasharray: 70,
            strokeDashoffset: 70,
            animation: 'bc-draw-circle 500ms cubic-bezier(0.65,0,0.35,1) 200ms forwards',
          }}
        />
        <polyline
          points="7 12.5 10.5 16 17 9"
          stroke="#34d399"
          strokeWidth="2.8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 18,
            strokeDashoffset: 18,
            animation: 'bc-draw-check 380ms cubic-bezier(0.65,0,0.35,1) 600ms forwards',
          }}
        />
      </svg>
    </div>
  )
}
